import { FAQItem } from '../types';

const CHATBOT_CONFIG = {
  faqUrl: '/assets/chatbot-faq.json',
  faqPage: '/FAQs.html',
  storageKey: 'olitask_chatbot_analytics_v2',
  highConfidence: 40,
  lowConfidence: 20,
};

const fallbackFaq: FAQItem[] = [
  { q: 'how to create an account sign up register', a: "Download the app, tap 'Sign Up', choose email/Google/Apple, enter details, verify email/phone, and complete your profile." },
  { q: 'forgot password reset', a: "Tap 'Forgot Password' on login, enter your email, open the reset link, and create a new password." },
  { q: 'how to post a task', a: "Open the app, tap 'Post a Task', describe the task, set budget and timeframe, choose category, add photos, and submit." },
  { q: 'become a helper how to', a: 'Sign up, complete your profile, verify ID, set skills and rates, then browse tasks and submit offers.' },
  { q: 'payment methods secure', a: 'You can pay by card, PayPal, Apple Pay, or Google Pay. Funds are held securely until completion.' },
  { q: 'download android link', a: 'https://play.google.com/store/apps/details?id=com.olitask.app' },
  { q: 'download ios link', a: 'https://apps.apple.com/us/app/olitask/id6756482938' },
  { q: 'contact support', a: 'Email help@oliveeb.com or use the in-app Help Center.' },
];

const stopwords = new Set(['a', 'an', 'the', 'is', 'are', 'am', 'to', 'for', 'of', 'in', 'on', 'at', 'it', 'me', 'my', 'and', 'or', 'do', 'i', 'you', 'we', 'can', 'how', 'what', 'when', 'where', 'why']);

const topicKeywords: Record<string, string[]> = {
  download: ['download', 'install', 'android', 'ios', 'iphone', 'app', 'store'],
  payment: ['payment', 'pay', 'paid', 'charge', 'fee', 'billing', 'cost', 'price'],
  helper: ['helper', 'worker', 'earn', 'income', 'apply', 'join'],
  task: ['task', 'post', 'request', 'job', 'gig'],
  account: ['account', 'register', 'signup', 'sign', 'login', 'password', 'verify'],
  safety: ['safe', 'safety', 'secure', 'trust', 'trusted', 'sos'],
};

function normalizeText(text: string): string {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stem(token: string): string {
  if (token.length > 5 && token.endsWith('ing')) return token.slice(0, -3);
  if (token.length > 4 && token.endsWith('ed')) return token.slice(0, -2);
  if (token.length > 4 && token.endsWith('es')) return token.slice(0, -2);
  if (token.length > 3 && token.endsWith('s')) return token.slice(0, -1);
  return token;
}

function tokenize(text: string): string[] {
  return normalizeText(text)
    .split(' ')
    .map(stem)
    .filter((token) => token && token.length > 1 && !stopwords.has(token));
}

function getPhrases(tokens: string[]): string[] {
  const phrases: string[] = [];
  for (let i = 0; i < tokens.length - 1; i += 1) {
    phrases.push(tokens[i] + ' ' + tokens[i + 1]);
  }
  return phrases;
}

function editDistance(a: string, b: string, maxDistance: number): number {
  const m = a.length;
  const n = b.length;
  if (Math.abs(m - n) > maxDistance) return maxDistance + 1;

  const dp = new Array(n + 1);
  for (let j = 0; j <= n; j += 1) dp[j] = j;

  for (let i = 1; i <= m; i += 1) {
    let prev = dp[0];
    dp[0] = i;
    let minInRow = dp[0];

    for (let j = 1; j <= n; j += 1) {
      const temp = dp[j];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + cost);
      prev = temp;
      if (dp[j] < minInRow) minInRow = dp[j];
    }

    if (minInRow > maxDistance) return maxDistance + 1;
  }

  return dp[n];
}

function tokenMatches(token: string, tokenList: string[]): boolean {
  if (tokenList.includes(token)) return true;
  if (token.length < 5) return false;
  for (let i = 0; i < tokenList.length; i += 1) {
    const candidate = tokenList[i];
    if (Math.abs(candidate.length - token.length) > 1) continue;
    if (editDistance(token, candidate, 1) <= 1) return true;
  }
  return false;
}

function prepareFaq(faq: FAQItem[]) {
  return faq.map((item) => {
    const normQ = normalizeText(item.q);
    const normA = normalizeText(item.a);
    const qTokens = tokenize(item.q);
    const aTokens = tokenize(item.a);
    return {
      item,
      normQ,
      normA,
      qTokens,
      aTokens,
      qSet: new Set(qTokens),
      aSet: new Set(aTokens),
    };
  });
}

function getTopicScore(userQuestionNorm: string, candidateNormQ: string): number {
  let score = 0;
  Object.keys(topicKeywords).forEach((topic) => {
    const keywords = topicKeywords[topic];
    const queryHasTopic = keywords.some((kw) => userQuestionNorm.includes(kw));
    if (queryHasTopic && candidateNormQ.includes(topic)) score += 24;
  });
  return score;
}

function scoreCandidate(
  userQuestionNorm: string,
  userTokens: string[],
  userPhrases: string[],
  prepared: any
): number {
  let score = 0;
  let tokenHits = 0;

  if (userQuestionNorm.length > 7 && prepared.normQ.includes(userQuestionNorm)) {
    score += 60;
  }

  for (let i = 0; i < userTokens.length; i += 1) {
    const token = userTokens[i];
    if (prepared.qSet.has(token)) {
      score += 18;
      tokenHits += 1;
    } else if (tokenMatches(token, prepared.qTokens)) {
      score += 10;
      tokenHits += 1;
    }

    if (prepared.aSet.has(token)) {
      score += 6;
    }
  }

  for (let i = 0; i < userPhrases.length; i += 1) {
    if (prepared.normQ.includes(userPhrases[i])) score += 20;
  }

  if (userTokens.length > 0) {
    score += Math.round((tokenHits / userTokens.length) * 12);
  }

  score += getTopicScore(userQuestionNorm, prepared.normQ);
  return score;
}

function findBestMatches(question: string, preparedFaq: any[]) {
  const userQuestionNorm = normalizeText(question);
  const userTokens = tokenize(question);
  const userPhrases = getPhrases(userTokens);

  const ranked = preparedFaq
    .map((prepared) => ({ prepared, score: scoreCandidate(userQuestionNorm, userTokens, userPhrases, prepared) }))
    .sort((a, b) => b.score - a.score);

  return ranked.slice(0, 3);
}

export async function loadFaq(): Promise<FAQItem[]> {
  if ((window as any).__olitaskFaqV2 && (window as any).__olitaskFaqV2.length) {
    return (window as any).__olitaskFaqV2;
  }

  try {
    const res = await fetch(CHATBOT_CONFIG.faqUrl, { cache: 'no-cache' });
    if (!res.ok) throw new Error('faq fetch failed');
    const data = await res.json();
    if (Array.isArray(data) && data.length) {
      (window as any).__olitaskFaqV2 = data;
      return data;
    }
  } catch (error) {
    console.error('Failed to load FAQ:', error);
  }
  return [...fallbackFaq];
}

export async function getAnswer(question: string, faqData: FAQItem[]): Promise<string> {
  let faq = faqData;
  if (!faq.length) {
    faq = await loadFaq();
  }

  const preparedFaq = prepareFaq(faq);
  const matches = findBestMatches(question, preparedFaq);
  const best = matches[0];

  if (best && best.score >= CHATBOT_CONFIG.highConfidence) {
    return best.prepared.item.a;
  }

  if (best && best.score >= CHATBOT_CONFIG.lowConfidence) {
    const options = matches.slice(0, 2).map((m) => 
      m.prepared.item.q.split(' ').slice(0, 5).join(' ')
    );
    return `I found a couple of close matches: ${options.join(' or ')}. Could you be more specific?`;
  }

  return "I am not fully sure about that yet. You can check our FAQ page or email help@oliveeb.com for personalized help.";
}

export function trackChatbotEvent(type: string, payload: any): void {
  try {
    const storageKey = CHATBOT_CONFIG.storageKey;
    const state = JSON.parse(localStorage.getItem(storageKey) || '{}');
    state.events = state.events || {};
    state.events[type] = (state.events[type] || 0) + 1;

    if (type === 'unanswered') {
      state.unanswered = state.unanswered || [];
      state.unanswered.push({ question: payload.question, ts: Date.now() });
      state.unanswered = state.unanswered.slice(-50);
    }

    if (type === 'feedback') {
      state.feedback = state.feedback || { yes: 0, no: 0 };
      if (payload.value === 'yes') state.feedback.yes += 1;
      if (payload.value === 'no') state.feedback.no += 1;
    }

    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (error) {
    // Ignore storage failures
  }
}